import { Link } from "react-router-dom";
import { Button } from "@redelair-sa/intiza-ui";
import { AgendaItem, AgendaResponse } from "../../../services/AgendaService";
import { TranslationService } from "../../../services/TranslationService";
import Empty from "../../shared/components/Empty";
import Paginator from "../../shared/Paginator";
import SearchInput from "../../shared/components/SearchInput";
import Tabs, { TabItem } from "../../shared/components/Tabs";
import { getLinkTo } from "../../shared/utils/LinkUtils";
import {
  formatAgendaDay,
  formatAgendaTime,
  translateWith
} from "../utils/agendaFormat";
import { AGENDA_PAGE_SIZE, AgendaTab } from "../utils/agendaUtils";
import AgendaItemRow from "./AgendaItemRow";

/** Route of the dashboard page, where the agenda panel lives. */
const DASHBOARD_ROUTE = "company";

interface AgendaFullListProps {
  agenda: AgendaResponse;
  tab: AgendaTab;
  counts: Record<AgendaTab, number>;
  /** Items of the current page. */
  pageItems: AgendaItem[];
  filteredCount: number;
  page: number;
  pageCount: number;
  canEdit: boolean;
  onTabChange: (tab: AgendaTab) => void;
  onSearch: (value: string) => void;
  onPageChange: (page: number) => void;
  onRegenerate: () => void;
  onToggleCompleted: (item: AgendaItem) => void;
  onOpen: (item: AgendaItem) => void;
}

/** Full agenda: header, tabs with counts, search, rows and pagination. */
const AgendaFullList = ({
  agenda,
  tab,
  counts,
  pageItems,
  filteredCount,
  page,
  pageCount,
  canEdit,
  onTabChange,
  onSearch,
  onPageChange,
  onRegenerate,
  onToggleCompleted,
  onOpen
}: AgendaFullListProps) => {
  const { translate } = TranslationService;
  const tabs: TabItem[] = [
    { id: "all", label: translate.AgendaTabAll, badge: counts.all },
    { id: "pending", label: translate.AgendaTabPending, badge: counts.pending },
    {
      id: "completed",
      label: translate.AgendaTabCompleted,
      badge: counts.completed
    }
  ];

  return (
    <div className="agenda-card">
      <div className="agenda-card__header">
        <div className="agenda-card__heading">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <Link
              to={getLinkTo(DASHBOARD_ROUTE)}
              className="agenda-back"
              aria-label={translate.GoBack}
              title={translate.GoBack}
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            </Link>
            <h2 className="agenda-card__title">{translate.AgendaDailyBadge}</h2>
            <span className="agenda-badge agenda-badge--ai">
              <i
                className="fa-solid fa-wand-magic-sparkles"
                aria-hidden="true"
              />{" "}
              {translate.AgendaGeneratedWithAI}
            </span>
          </div>
          <div className="agenda-card__subtitle">
            {translateWith("AgendaGeneratedAt", {
              date: formatAgendaDay(agenda.agendaDate),
              time: formatAgendaTime(agenda.generatedAt)
            })}
          </div>
        </div>
        {canEdit && (
          <Button variant="secondary" size="sm" onClick={onRegenerate}>
            {translate.AgendaRegenerateFull}
          </Button>
        )}
      </div>

      <div className="agenda-toolbar">
        <Tabs
          items={tabs}
          activeId={tab}
          onChange={(id) => onTabChange(id as AgendaTab)}
        />
        <SearchInput
          divClass="agenda-toolbar__search"
          placeholder={translate.AgendaSearchPlaceholder}
          onSearch={onSearch}
        />
      </div>

      <div className="agenda-list">
        {pageItems.length === 0 && <Empty />}
        {pageItems.map((item) => (
          <AgendaItemRow
            key={item.itemId}
            item={item}
            variant="full"
            canEdit={canEdit}
            onToggleCompleted={onToggleCompleted}
            onOpen={onOpen}
          />
        ))}
      </div>

      <Paginator
        pageCount={pageCount}
        currentPage={page}
        setCurrentPage={onPageChange}
        pageSize={AGENDA_PAGE_SIZE}
        response={{ pageSize: AGENDA_PAGE_SIZE, itemCount: filteredCount }}
      />

      <p className="agenda-note">{translate.AgendaPersonalNote}</p>
    </div>
  );
};

export default AgendaFullList;
